import { Link } from "react-router-dom";
import { SectionLink } from "../../../components/SectionLink.tsx";
import { assets, FEATURED_EVENT_SLUG, HOST_ROUTE } from "../data.ts";
import { ImageSlot } from "./ImageSlot.tsx";
import { Reveal } from "./Reveal.tsx";
import { IconArrowDown, IconArrowRight } from "./icons.tsx";
import { RestoMagShowcase } from "./RestoMagShowcase.tsx";

/** Hero — variant A (cinematic full-bleed), the design's default heroVariant. */
export function Hero() {
  return (
    <section
      aria-label="Hero"
      className="relative flex min-h-screen flex-col overflow-hidden bg-ink"
    >
      {/* atmosphere + hero photo */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          data-px={0.04}
          className="absolute inset-x-0 -inset-y-[6%] h-[112%] w-full"
          style={{
            background:
              "radial-gradient(62% 58% at 20% 82%,rgba(221,42,2,.32),transparent 58%),radial-gradient(54% 52% at 84% 14%,rgba(74,102,134,.22),transparent 55%),linear-gradient(180deg,#120d0a 0%,#1f1611 46%,#0c0b0a 100%)",
          }}
        />
        <div
          className="pointer-events-none absolute bottom-[-8%] left-[6%] h-[62%] w-[48%] animate-glow blur-[26px]"
          style={{ background: "radial-gradient(circle,rgba(221,42,2,.30),transparent 62%)" }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-50"
          style={{
            backgroundImage: "radial-gradient(rgba(255,255,255,.045) 1px,transparent 1.5px)",
            backgroundSize: "32px 32px",
          }}
        />
        <ImageSlot
          src={assets.hero}
          alt="A classic Mustang rolling into a car show"
          parallax={0.08}
          className="absolute inset-x-0 top-[-9%] h-[118%] w-full !bg-transparent"
        />
      </div>

      {/* cinematic scrims */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg,rgba(12,11,10,.74) 0%,rgba(12,11,10,.4) 20%,rgba(12,11,10,.34) 48%,rgba(12,11,10,.8) 78%,rgba(12,11,10,.98) 100%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: "linear-gradient(90deg,rgba(12,11,10,.88) 0%,rgba(12,11,10,.52) 28%,rgba(12,11,10,0) 62%)",
        }}
      />
      <div
        className="pointer-events-none absolute inset-0"
        style={{ background: "radial-gradient(120% 80% at 12% 92%,rgba(12,11,10,.6),transparent 55%)" }}
      />

      {/* Top padding clears the fixed announcement bar (--ann-bar-h, wraps on mobile) plus the
          fixed nav below it; without this the top row collides with the nav on small screens. */}
      <div className="relative mx-auto flex w-full max-w-shell flex-1 flex-col justify-between px-5 pb-24 pt-[calc(var(--ann-bar-h,0px)_+_6rem)] md:px-8 md:pt-[calc(var(--ann-bar-h,0px)_+_7rem)] lg:px-12 lg:pb-28">
        {/* top row: teaser */}
        <Reveal className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <Link
            to={`/${FEATURED_EVENT_SLUG}`}
            aria-label="Up next: EyeKandy Kustoms Open House — Jul 1, Vancouver, BC"
            className="flex w-full max-w-full flex-col gap-[7px] rounded-[13px] border border-white/[.12] bg-[rgba(20,18,16,.55)] px-[18px] py-3.5 text-white no-underline backdrop-blur-[10px] transition hover:border-white/30 hover:bg-[rgba(20,18,16,.72)] sm:max-w-[300px]"
          >
            <span className="font-mono text-[10px] uppercase tracking-[.2em] text-flame">Up next · Canada Day</span>
            <div className="text-[14.5px] font-bold leading-[1.25]">EyeKandy Kustoms Open House</div>
            <div className="text-xs text-white/60">Jul 1 · Vancouver, BC</div>
          </Link>
          {/* Desktop showcase — hidden on small screens */}
          <div className="hidden lg:block">
            <RestoMagShowcase />
          </div>
        </Reveal>

        {/* bottom block */}
        <div className="max-w-[1000px]">
          <Reveal className="mb-[26px] font-mono text-[13px] font-medium uppercase tracking-[.3em] text-flame">
            The home of enthusiast events
          </Reveal>
          <Reveal
            as="h1"
            delay={120}
            className="m-0 mb-7 font-archivo text-[clamp(40px,11vw,142px)] font-extrabold leading-[.9] tracking-[-.035em] text-white"
          >
            Every event
            <br />
            has a <span className="text-white">Story</span>
          </Reveal>
          <Reveal
            as="p"
            delay={180}
            className="m-0 mb-[38px] max-w-[50ch] text-[clamp(17px,1.5vw,21px)] leading-[1.5] text-white/[.74]"
          >
            Discover the best automotive, aviation and marine events across North America — and the people, machines and
            culture behind every one.
          </Reveal>

          <Reveal delay={240} className="mb-[30px] flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:gap-3.5">
            <SectionLink
              section="featured"
              className="inline-flex w-full items-center justify-center gap-2.5 rounded-xl border border-flame bg-flame px-[30px] py-4 text-base font-bold text-white no-underline transition hover:-translate-y-0.5 hover:border-flame-dark hover:bg-flame-dark sm:w-auto"
            >
              Explore Events <IconArrowRight size={17} />
            </SectionLink>
            <Link
              to={HOST_ROUTE}
              className="inline-flex w-full items-center justify-center gap-2.5 rounded-xl border border-white/[.24] bg-white/[.06] px-7 py-4 text-base font-semibold text-white no-underline backdrop-blur-[8px] transition hover:bg-white/[.14] sm:w-auto"
            >
              Host an Event
            </Link>
          </Reveal>
          {/* Mobile showcase — visible below lg breakpoint */}
          <Reveal delay={300} className="lg:hidden">
            <RestoMagShowcase />
          </Reveal>
        </div>
      </div>

      <div className="absolute bottom-[30px] right-5 hidden animate-float flex-col items-center gap-2 sm:flex md:right-8 lg:right-12">
        <span className="font-mono text-[10px] uppercase tracking-[.32em] text-white/50">Land · Sea · Air</span>
        <IconArrowDown size={16} className="text-white/50" />
      </div>
    </section>
  );
}
